import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import {
  getAllUsers,
  getFoundersFirstJournalsForOptimalPaceAnalysis,
  mergeInsatiableJournalEntriesWithDoryRows,
  normalizeDoryJournalRowToInsatiableEntry,
  collectHistoricalOptimalPaceValues
} from '../../services/funnelService';
import DistributionChart from './DistributionChart';

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error};
  color: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
  line-height: ${theme.typography.lineHeights.normal};
`;

const WarningMessage = styled.div`
  background: ${theme.colors.doryBeige};
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.doryLighterBlue};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-bottom: ${theme.spacing.lg};
  line-height: ${theme.typography.lineHeights.normal};
`;

const RefreshButton = styled.button`
  background: transparent;
  color: ${theme.colors.doryDarkerBlue};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: background ${theme.animation.durations.fast}, color ${theme.animation.durations.fast};
  white-space: nowrap;
  line-height: 1.3;

  &:hover:not(:disabled) {
    background: ${theme.colors.doryBeige};
    color: ${theme.colors.doryBlue};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h1};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const StatLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
`;

const isProductionUser = (user) => {
  const hostname = user.hostname || '';

  if (!Object.prototype.hasOwnProperty.call(user, 'hostname')) {
    return true;
  }

  return (
    !hostname.includes('localhost') &&
    !hostname.includes('127.0.0.1') &&
    !hostname.includes('test') &&
    hostname.length > 0
  );
};

const addOptimalPaceValue = (numericHours, optimalPaceValues, distribution) => {
  if (numericHours === undefined || numericHours === null) {
    return;
  }

  const hours = Number(numericHours);
  if (Number.isNaN(hours)) {
    return;
  }

  const roundedHours = Math.round(hours);
  const cappedHours = Math.min(18, Math.max(0, roundedHours));

  optimalPaceValues.push(hours);
  distribution[cappedHours]++;
};

const normalizeShortcode = (value) => {
  if (value == null) return '';
  const trimmed = String(value).trim().toUpperCase();
  return trimmed;
};

const OptimalPaceAnalysisScreen = () => {
  const [optimalPaceData, setOptimalPaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const analyzeOptimalPaceData = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Fetching Insatiable + founders-first journals for historical optimal pace analysis...');
      const [users, foundersFirstResult] = await Promise.all([
        getAllUsers(),
        getFoundersFirstJournalsForOptimalPaceAnalysis().catch((ffError) => {
          console.error('Founders-first journals fetch failed:', ffError);
          return {
            users: [],
            error: ffError.message || 'Failed to load founders-first journals'
          };
        })
      ]);

      const productionUsers = users.filter(isProductionUser);
      const ffUsers = Array.isArray(foundersFirstResult.users) ? foundersFirstResult.users : [];

      const ffByShortCode = new Map();
      ffUsers.forEach((ffUser) => {
        const shortCode = normalizeShortcode(ffUser.shortCode);
        if (!shortCode) return;
        if (!ffByShortCode.has(shortCode)) {
          ffByShortCode.set(shortCode, []);
        }
        ffByShortCode.get(shortCode).push(ffUser);
      });

      console.log(
        `Back-calculating optimal pace across ${productionUsers.length} Insatiable production users` +
          ` and ${ffUsers.length} founders-first users with journals`
      );

      const optimalPaceValues = [];
      const distribution = {};
      for (let hour = 0; hour <= 18; hour++) {
        distribution[hour] = 0;
      }

      let insatiableReadings = 0;
      let foundersFirstOnlyReadings = 0;
      let skippedDefault = 0;
      let usersWithReadings = 0;
      const handledFfUserIds = new Set();
      const handledShortcodes = new Set();

      productionUsers.forEach((user) => {
        const shortcode = normalizeShortcode(user.shortcode || user.id);
        const ffBundles = shortcode ? ffByShortCode.get(shortcode) || [] : [];
        const doryRows = ffBundles.flatMap((bundle) => {
          handledFfUserIds.add(bundle.userId);
          return bundle.entries || [];
        });
        if (shortcode) {
          handledShortcodes.add(shortcode);
        }

        const mergedEntries = mergeInsatiableJournalEntriesWithDoryRows(
          user.journalEntries || [],
          doryRows
        );
        const collected = collectHistoricalOptimalPaceValues(mergedEntries);
        skippedDefault += collected.skippedDefault;

        if (collected.values.length === 0) {
          return;
        }

        usersWithReadings++;
        collected.values.forEach((hours) => {
          addOptimalPaceValue(hours, optimalPaceValues, distribution);
          insatiableReadings++;
        });
      });

      ffUsers.forEach((ffUser) => {
        if (handledFfUserIds.has(ffUser.userId)) {
          return;
        }
        const shortCode = normalizeShortcode(ffUser.shortCode);
        if (shortCode && handledShortcodes.has(shortCode)) {
          return;
        }

        const normalizedEntries = (ffUser.entries || [])
          .map((row) => normalizeDoryJournalRowToInsatiableEntry(row))
          .filter(Boolean);
        const collected = collectHistoricalOptimalPaceValues(normalizedEntries);
        skippedDefault += collected.skippedDefault;

        if (collected.values.length === 0) {
          return;
        }

        usersWithReadings++;
        collected.values.forEach((hours) => {
          addOptimalPaceValue(hours, optimalPaceValues, distribution);
          foundersFirstOnlyReadings++;
        });
      });

      const totalReadings = optimalPaceValues.length;
      const averageOptimalPace =
        totalReadings > 0
          ? optimalPaceValues.reduce((sum, val) => sum + val, 0) / totalReadings
          : 0;
      const sortedValues = [...optimalPaceValues].sort((a, b) => a - b);
      const medianOptimalPace =
        totalReadings > 0 ? sortedValues[Math.floor(totalReadings / 2)] : 0;

      let mostCommonHour = 0;
      let maxCount = 0;
      for (let hour = 0; hour <= 18; hour++) {
        if (distribution[hour] > maxCount) {
          maxCount = distribution[hour];
          mostCommonHour = hour;
        }
      }

      const analysisData = {
        distribution,
        totalReadings,
        // Keep totalUsers alias for empty-state checks during transition
        totalUsers: totalReadings,
        averageOptimalPace,
        medianOptimalPace,
        mostCommonHour,
        maxCount,
        rawValues: optimalPaceValues,
        insatiableReadings,
        foundersFirstOnlyReadings,
        skippedDefault,
        usersWithReadings,
        foundersFirstError: foundersFirstResult.error || null
      };

      setOptimalPaceData(analysisData);
      console.log('Historical optimal pace analysis completed:', analysisData);
    } catch (err) {
      const errorMessage = err.message || 'Failed to load optimal pace analysis data. Please try again.';
      setError(`Error: ${errorMessage}`);
      console.error('Error loading optimal pace analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    analyzeOptimalPaceData();
  }, []);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Back-calculating optimal pace across all journals...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <RefreshButton type="button" onClick={analyzeOptimalPaceData}>
          Retry
        </RefreshButton>
      </Container>
    );
  }

  if (!optimalPaceData || optimalPaceData.totalReadings === 0) {
    return (
      <Container>
        <Header>
          <Title>Optimal Pace Distribution Analysis</Title>
          <RefreshButton type="button" onClick={analyzeOptimalPaceData}>
            Refresh
          </RefreshButton>
        </Header>
        {optimalPaceData?.foundersFirstError && (
          <WarningMessage>
            Founders-first data unavailable: {optimalPaceData.foundersFirstError}
          </WarningMessage>
        )}
        <NoDataMessage>
          No back-calculated optimal pace readings available from production journals.
        </NoDataMessage>
      </Container>
    );
  }

  const {
    distribution,
    totalReadings,
    averageOptimalPace,
    medianOptimalPace,
    mostCommonHour,
    maxCount,
    insatiableReadings,
    foundersFirstOnlyReadings,
    skippedDefault,
    usersWithReadings,
    foundersFirstError
  } = optimalPaceData;

  return (
    <Container>
      <Header>
        <Title>Optimal Pace Distribution Analysis</Title>
        <RefreshButton type="button" onClick={analyzeOptimalPaceData}>
          Refresh
        </RefreshButton>
      </Header>

      {foundersFirstError && (
        <WarningMessage>
          Founders-first data unavailable (showing Insatiable only): {foundersFirstError}
        </WarningMessage>
      )}

      <DistributionChart distribution={distribution} maxCount={maxCount} />

      <SummaryStats>
        <StatCard>
          <StatValue>{totalReadings}</StatValue>
          <StatLabel>Optimal Pace Readings</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{usersWithReadings}</StatValue>
          <StatLabel>Users Contributing Readings</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{insatiableReadings}</StatValue>
          <StatLabel>From Insatiable Histories</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{foundersFirstOnlyReadings}</StatValue>
          <StatLabel>From founders-first-only Histories</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{skippedDefault}</StatValue>
          <StatLabel>Excluded (Default Journal)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{averageOptimalPace.toFixed(1)}</StatValue>
          <StatLabel>Average Optimal Pace</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{medianOptimalPace.toFixed(1)}</StatValue>
          <StatLabel>Median Optimal Pace</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{mostCommonHour}</StatValue>
          <StatLabel>Most Common Optimal Pace</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{maxCount}</StatValue>
          <StatLabel>Readings at Most Common Pace</StatLabel>
        </StatCard>
      </SummaryStats>
    </Container>
  );
};

export default OptimalPaceAnalysisScreen;
